import React, { useState } from 'react';

import { useObserver } from 'mobx-react-lite';
import { Table, Button } from '@bringg/react-components';
import map from 'lodash/map';
import { useTranslation } from 'react-i18next';
import {
	ENABLE_WEBHOOK_AUTH_ADDITIONAL_HEADERS_FF,
	useWebhooksApiStore,
	Webhook,
	webhookTypeTranslationKey
} from '@bringg-frontend/webhooks-api-internal-stores';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import WebhooksEdit from '../webhooks-edit/webhooks-edit';

const WebhooksTable: React.FC = () => {
	const { t } = useTranslation();
	const { webhooksAppStore } = useWebhooksApiStore();
	const [activeWebhook, setActiveWebhook] = useState(null);
	const enableWebhookAuthAdditionalHeaders = useHasFeatureFlag(ENABLE_WEBHOOK_AUTH_ADDITIONAL_HEADERS_FF);

	const openModal = webhook => {
		setActiveWebhook(Webhook.cloneWebhook(webhook));
	};

	const closeModal = () => {
		setActiveWebhook(null);
	};

	const getColumns = () => {
		const { applicationConfig } = webhooksAppStore;

		const columns = [
			{
				dataIndex: 'key',
				title: t('WEBHOOK_APP.TABLE.COLUMN_WEBHOOK_KEY_TITLE', { defaultValue: 'Webhooks Name' }),
				width: '20%',
				render: key => {
					const webhookConfig = applicationConfig[key];
					return (
						<span>
							{webhookConfig
								? t(webhookTypeTranslationKey(webhookConfig.key), { defaultValue: webhookConfig.title })
								: key}
						</span>
					);
				}
			},
			{
				dataIndex: 'conditionKey',
				width: '20%',
				title: t('WEBHOOK_APP.TABLE.COLUMN_FILTER_TITLE', { defaultValue: 'Filter by task field and Value' }),
				render: (conditionKey, webhook) => (
					<span>{webhook.conditionKey ? `${webhook.conditionKey}: ${webhook.conditionValue}` : ''}</span>
				)
			},
			{
				dataIndex: 'urls',
				width: '55%',
				title: t('WEBHOOK_APP.TABLE.COLUMN_URL_TITLE', { defaultValue: 'URL' }),
				render: urls => (
					<div>
						{map(urls, ([uuid, url]) => {
							return <div key={uuid}>{url}</div>;
						})}
					</div>
				)
			},
			{
				className: 'actions',
				dataIndex: 'action',
				title: t('WEBHOOK_APP.TABLE.COLUMN_ACTION_TITLE', { defaultValue: 'Actions' }),
				render: (text, webhook) => (
					<Button onClick={() => openModal(webhook)} size="small" type="default">
						{t('WEBHOOK_APP.TABLE.EDIT_WEBHOOK_BUTTON', { defaultValue: 'Edit' })}
					</Button>
				)
			}
		];

		if (enableWebhookAuthAdditionalHeaders) {
			columns.splice(1, 0, {
				dataIndex: 'webhookAuthentication',
				width: '20%',
				title: t('WEBHOOK_APP.TABLE.COLUMN_WEBHOOK_AUTHENTICATION_TITLE', {
					defaultValue: 'Webhook authentication'
				}),
				render: (text, webhook: Webhook) => <span>{webhook.webhookAuthenticationConfiguration?.name}</span>
			});
		}

		return columns;
	};

	return useObserver(() => {
		const { webhooks } = webhooksAppStore;

		return (
			<div className="webhooks-table">
				<Table
					bordered
					dataSource={webhooks}
					columns={getColumns()}
					pagination={false}
					rowKey="uuid"
					size="middle"
				/>
				{activeWebhook ? <WebhooksEdit closeEdit={closeModal} webhook={activeWebhook} /> : ''}
			</div>
		);
	});
};

export default WebhooksTable;
